import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "components/confirm_dialog";
import OnlyConfirmDialog from "components/onlyConfirm_dialog";
import SelectCategoryDesktop from "components/inquiry/select_category_d";
import { createInquiry } from "firebase_/firebase_function/firebase_inquiry";
import OnlyLoadingScreen from "components/loading_screen";
import { useUser } from "util/context/user_context";

const C = {
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  greenBorder: "#A7F3D0",
  bg: "#F7FAF7",
};

const TITLE_LIMIT = 30;
const BODY_LIMIT = 300;
const BODY_LINE_LIMIT = 30;

export default function DesktopInquiryWriteScreen() {
  const { userData } = useUser();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const [onlyConfirmModal, setOnlyConfirmModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const [pageLoading, setPageLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectCategory, setSelectCategory] = useState("");

  const handleAddInquiry = async () => {
    setPageLoading(true);
    const result = await createInquiry(userData, title, body, selectCategory);
    if (result) {
      setOnlyConfirmModal(true);
      setModalMessage("문의 등록에 성공했습니다.");
      navigate("/inquiry");
    } else {
      setOnlyConfirmModal(true);
      setModalMessage("문의 등록에 실패했습니다.");
    }
    setPageLoading(false);
  };

  const pressedSelect = (cat) => setSelectCategory(cat);

  const submit = () => {
    if (!title.trim() || !body.trim()) {
      setOnlyConfirmModal(true);
      setModalMessage("제목 또는 내용이 비어있습니다.");
      return;
    }
    if (!selectCategory) {
      setOnlyConfirmModal(true);
      setModalMessage("분야를 선택해주세요.");
      return;
    }
    setConfirmModal(true);
  };

  return (
    <div
      style={{
        fontFamily:
          'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, sans-serif',
        maxWidth: 1100,
        margin: "0 auto",
        padding: "32px 20px 80px",
        minHeight: "100vh",
        background: C.bg,
        display: "flex",
        flexDirection: "column",
        gap: 20,
      }}
    >
      {/* Back link */}
      <button
        type="button"
        onClick={() => navigate("/inquiry")}
        style={{
          alignSelf: "flex-start",
          background: "transparent",
          border: "none",
          color: C.sub,
          fontSize: 13,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: 0,
        }}
      >
        ← 문의 내역으로
      </button>

      {/* Header */}
      <div>
        <div
          style={{
            fontSize: 26,
            fontWeight: 800,
            color: C.ink,
            letterSpacing: "-0.02em",
          }}
        >
          문의 작성하기
        </div>
        <div style={{ marginTop: 4, fontSize: 13, color: C.sub }}>
          궁금한 점이 있으시면 언제든 문의해 주세요. 영업일 기준 빠르게 답변드립니다.
        </div>
      </div>

      <div
        style={{
          maxWidth: 760,
          width: "100%",
          margin: "0 auto",
          background: "#fff",
          border: `1px solid ${C.line}`,
          borderRadius: 16,
          padding: "28px 28px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        {/* 분야 */}
        <Field label="분야" required>
          <button
            type="button"
            onClick={() => setIsDialogOpen(true)}
            style={{
              padding: "10px 16px",
              borderRadius: 10,
              border: `1px solid ${selectCategory ? C.greenBorder : C.line}`,
              background: selectCategory ? C.greenBg : "#fff",
              color: selectCategory ? C.greenDark : C.body,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              alignSelf: "flex-start",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {selectCategory || "분야 선택하기"}
            <span style={{ fontSize: 11, opacity: 0.6 }}>▾</span>
          </button>
        </Field>

        {/* 제목 */}
        <Field
          label="제목"
          required
          counter={`${title.length}/${TITLE_LIMIT}`}
        >
          <input
            value={title}
            onChange={(e) => {
              if (e.target.value.length > TITLE_LIMIT) {
                setOnlyConfirmModal(true);
                setModalMessage(`제목은 최대 ${TITLE_LIMIT}자까지 작성할 수 있습니다.`);
                return;
              }
              setTitle(e.target.value);
            }}
            placeholder="문의 제목을 입력하세요"
            style={inputStyle}
          />
        </Field>

        {/* 내용 */}
        <Field
          label="내용"
          required
          counter={`${body.length}/${BODY_LIMIT}`}
        >
          <textarea
            value={body}
            onChange={(e) => {
              const lines = e.target.value.split("\n");
              if (lines.length > BODY_LINE_LIMIT) {
                setOnlyConfirmModal(true);
                setModalMessage(
                  `내용은 최대 ${BODY_LINE_LIMIT}줄 또는 ${BODY_LIMIT}자까지 작성가능합니다.`,
                );
                return;
              }
              if (e.target.value.length > BODY_LIMIT) {
                setOnlyConfirmModal(true);
                setModalMessage(
                  `내용은 최대 ${BODY_LINE_LIMIT}줄 또는 ${BODY_LIMIT}자까지 작성가능합니다.`,
                );
                return;
              }
              setBody(e.target.value);
            }}
            placeholder="문의 내용을 자세히 작성해주세요."
            rows={8}
            style={{
              ...inputStyle,
              minHeight: 200,
              resize: "vertical",
              fontFamily: "inherit",
              lineHeight: 1.6,
              padding: "12px 14px",
            }}
          />
        </Field>

        {/* Submit */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 4 }}>
          <button
            type="button"
            onClick={() => navigate("/inquiry")}
            style={{
              padding: "12px 22px",
              borderRadius: 10,
              border: `1px solid ${C.line}`,
              background: "#fff",
              color: C.body,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            취소
          </button>
          <button
            type="button"
            onClick={submit}
            style={{
              padding: "12px 26px",
              borderRadius: 10,
              border: "none",
              background: C.greenDark,
              color: "#fff",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            문의 등록
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmModal}
        onClose={() => setConfirmModal(false)}
        onConfirm={() => {
          setConfirmModal(false);
          handleAddInquiry();
        }}
        message={"해당문의를 등록하시겠습니까?"}
      />
      <OnlyConfirmDialog
        open={onlyConfirmModal}
        onClose={() => setOnlyConfirmModal(false)}
        message={modalMessage}
      />
      <SelectCategoryDesktop
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={pressedSelect}
      />
      <OnlyLoadingScreen isOpen={pageLoading} />
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 10,
  border: `1px solid ${C.line}`,
  fontSize: 14,
  color: C.ink,
  background: "#fff",
  outline: "none",
  fontFamily: "inherit",
  boxSizing: "border-box",
};

function Field({ label, required, counter, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13, color: C.body, fontWeight: 700 }}>
          {label} {required && <span style={{ color: "#EF4444" }}>*</span>}
        </span>
        {counter && (
          <span style={{ fontSize: 12, color: C.sub }}>{counter}</span>
        )}
      </div>
      {children}
    </div>
  );
}
